// Excel експортът на „Дневник на резервациите".
//
// Редовете идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`):
// файлът повтаря екрана. БЕЗ собствен времеви печат — `exportToExcel` лепи
// `_${stamp}`. Минутите излизат като ЧИСЛА (не „1 ч 16 мин"), за да се смятат
// в Excel; празната стойност е празна клетка, не 0.

import type { TFunction } from "i18next";

import { parseDecimal } from "../../../../components/dataTable";
import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import type { ReservationJournalRow } from "../../../../api/reportsReservations";
import {
  formatHour,
  formatMomentWithDate,
  reservationNoteText,
  statusLabel,
} from "./reservationReportFormat";

type Options = {
  rows: readonly ReservationJournalRow[];
  t: TFunction;
};

export const exportJournal = ({ rows, t }: Options) => {
  const columns: ExcelColumn<ReservationJournalRow>[] = [
    { header: t("reportsPage.rvColumnTime"), value: (row) => formatMomentWithDate(row.start_at), width: 18 },
    { header: t("reportsPage.rvExportEnd"), value: (row) => formatHour(row.end_at), width: 8 },
    { header: t("reportsPage.rvColumnName"), value: (row) => row.guest_name, width: 24 },
    { header: t("reportsPage.rvColumnPhone"), value: (row) => row.phone, width: 16 },
    { header: t("reportsPage.rvColumnGuests"), value: (row) => row.guests, width: 8 },
    { header: t("reportsPage.rvColumnTables"), value: (row) => row.table_names.join(", "), width: 12 },
    { header: t("reportsPage.rvColumnStatus"), value: (row) => statusLabel(t, row.status), width: 14 },
    // Веднага след статуса, както на екрана; широка, защото Excel спира текста
    // на следващата непразна клетка (пренасяне на редове SheetJS не записва).
    { header: t("reportsPage.rvColumnNote"), value: (row) => reservationNoteText(row.note) ?? "", width: 40 },
    { header: t("reportsPage.rvExportDurationMinutes"), value: (row) => row.duration_minutes, width: 16 },
    { header: t("reportsPage.rvColumnSeatedAt"), value: (row) => formatMomentWithDate(row.seated_at), width: 18 },
    { header: t("reportsPage.rvColumnReleasedAt"), value: (row) => formatMomentWithDate(row.released_at), width: 18 },
    { header: t("reportsPage.rvExportStayMinutes"), value: (row) => row.seated_minutes, width: 16 },
    { header: t("reportsPage.rvExportBillName"), value: (row) => row.bill_name ?? "", width: 10 },
    {
      header: t("reportsPage.rvColumnBill"),
      value: (row) => (row.bill_total === null ? null : parseDecimal(row.bill_total)),
      width: 12,
    },
    { header: t("reportsPage.rvColumnCreatedBy"), value: (row) => row.created_by_name ?? "", width: 18 },
    { header: t("reportsPage.rvColumnSeatedBy"), value: (row) => row.first_order_by_name ?? "", width: 18 },
    { header: t("reportsPage.rvColumnCreatedAt"), value: (row) => formatMomentWithDate(row.created_at), width: 18 },
  ];

  exportToExcel<ReservationJournalRow>({
    filename: t("reportsPage.rvJournal"),
    sheetName: t("reportsPage.rvJournal"),
    columns,
    rows: [...rows],
  });
};
